import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useInView } from 'react-intersection-observer';
import { useBitcoinFeed } from '@/hooks/useBitcoinFeed';
import { NoteCard } from '@/components/NoteCard';
import { ReplyDialog } from '@/components/ReplyDialog';
import { LoginArea } from '@/components/auth/LoginArea';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Zap, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { NostrEvent } from '@nostrify/nostrify';

const Index = () => {
  useSeoMeta({
    title: 'Bitcoin Nostr Feed — See what people are saying about Bitcoin',
    description:
      'Real-time feed of everything people are saying about Bitcoin on the Nostr network. Decentralized, censorship-resistant, and open.',
  });

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, refetch } =
    useBitcoinFeed();

  const { ref: sentinelRef, inView } = useInView({ threshold: 0 });

  // Reply dialog state
  const [replyEvent, setReplyEvent] = useState<NostrEvent | null>(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);

  const handleReply = useCallback((event: NostrEvent) => {
    setReplyEvent(event);
    setReplyDialogOpen(true);
  }, []);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Deduplicate events by ID
  const posts = useMemo(() => {
    const seen = new Set<string>();
    return (
      data?.pages.flat().filter((event) => {
        if (!event.id || seen.has(event.id)) return false;
        seen.add(event.id);
        return true;
      }) ?? []
    );
  }, [data?.pages]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
        <div className="container flex items-center justify-between h-16 px-4 md:px-6">
          {/* Logo / Title */}
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-bitcoin text-white">
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight text-foreground leading-none">
                Bitcoin Feed
              </h1>
              <p className="text-xs text-muted-foreground leading-none mt-0.5">
                Powered by Nostr
              </p>
            </div>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-foreground"
              onClick={() => refetch()}
              title="Refresh feed"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <LoginArea className="max-w-48" />
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="relative overflow-hidden bg-gradient-to-br from-bitcoin/5 via-bitcoin/10 to-transparent border-b border-border/30">
        <div className="container px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-2xl space-y-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-bitcoin/10 border border-bitcoin/20 px-3 py-1 text-xs font-medium text-bitcoin">
                <Zap className="h-3 w-3" />
                Live on Nostr
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Everything people are saying
              <br />
              <span className="text-bitcoin">about Bitcoin</span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-lg leading-relaxed">
              A real-time, censorship-resistant feed of Bitcoin conversations on the
              Nostr network. No algorithms, no ads — just pure signal from the
              decentralized web.
            </p>
          </div>
        </div>
        {/* Decorative circles */}
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-bitcoin/5 blur-3xl" />
        <div className="absolute -right-10 -bottom-20 h-48 w-48 rounded-full bg-bitcoin/8 blur-2xl" />
      </div>

      {/* Feed */}
      <main className="container px-4 md:px-6 py-8">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Loading skeleton */}
          {isLoading && (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Card key={i} className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-4/5" />
                        <Skeleton className="h-4 w-3/5" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Error state */}
          {isError && !isLoading && (
            <Card className="border-dashed">
              <CardContent className="py-12 px-8 text-center">
                <p className="text-muted-foreground max-w-sm mx-auto mb-4">
                  Couldn't load the Bitcoin feed. This might be a relay connection issue.
                </p>
                <Button
                  variant="outline"
                  onClick={() => refetch()}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try again
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Empty state */}
          {!isLoading && !isError && posts.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="py-12 px-8 text-center">
                <div className="flex justify-center mb-4">
                  <div className="flex items-center justify-center h-12 w-12 rounded-full bg-bitcoin/10">
                    <Zap className="h-6 w-6 text-bitcoin" />
                  </div>
                </div>
                <p className="text-muted-foreground max-w-sm mx-auto">
                  No Bitcoin posts found yet. Try refreshing — new notes appear
                  in real time as they're published to Nostr relays.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Post cards */}
          {!isLoading &&
            posts.map((post) => (
              <NoteCard key={post.id} event={post} onReply={handleReply} />
            ))}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="py-4">
            {isFetchingNextPage && (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Card key={i} className="border-border/50">
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-3/4" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {!hasNextPage && posts.length > 0 && (
              <p className="text-center text-sm text-muted-foreground py-2">
                You've reached the end of the feed
              </p>
            )}
          </div>
        </div>
      </main>

      {/* Reply dialog */}
      <ReplyDialog
        event={replyEvent}
        open={replyDialogOpen}
        onOpenChange={setReplyDialogOpen}
      />

      {/* Footer */}
      <footer className="border-t border-border/30 py-8">
        <div className="container px-4 md:px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-bitcoin" />
            <span>Bitcoin Nostr Feed</span>
            <span className="text-border">·</span>
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
            >
              Vibed with Shakespeare
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://nostr.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
            >
              What is Nostr?
            </a>
            <a
              href="https://bitcoin.org"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
            >
              What is Bitcoin?
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
